<script setup>
import {t} from "@/controllerLK/GlobusLocalization";
import {computed, nextTick, onBeforeUpdate, onMounted, ref, watch} from "vue";
import {entriesMock} from "@/pages/LKPage/LKWindow/LKProjectWindow/ProjectModal/historyMockData";

const searchTerm = ref("");
const selectAll = ref(false);
const entries = ref(entriesMock);

// refs & state for scroll detection
const body = ref(null);
const hasScroll = ref(false);

// list of columns
const columnList = [
  { name: t("project_history_modal.column.object"), id: "object" },
  { name: t("project_history_modal.column.action"), id: "action" },
  { name: t("project_history_modal.column.was"), id: "was" },
  { name: t("project_history_modal.column.became"), id: "became" },
  { name: t("project_history_modal.column.user"), id: "user" },
  { name: t("project_history_modal.column.time"), id: "time" }
];

// select-all watcher
watch(selectAll, val => {
  entries.value.forEach(e => e.selected = val);
});

// filter logic
const filteredEntries = computed(() => {
  const term = searchTerm.value.trim().toLowerCase();
  return term
      ? entries.value.filter(entry =>
          Object.values(entry).some(v => String(v).toLowerCase().includes(term))
      )
      : entries.value;
});

// check if tbody needs scrollbar
function checkScroll() {
  const el = body.value;
  if (el) {
    hasScroll.value = el.scrollHeight > el.clientHeight;
  }
}

// on mount and before each update, recalc
onMounted(() => nextTick(checkScroll));
onBeforeUpdate(() => nextTick(checkScroll));
</script>

<template>
  <div class="project-history">
    <InputSearchTertiary v-model="searchTerm" />

    <div class="history-table">
      <table class="history-table__table">
        <thead class="history-table__header">
          <tr>
            <th class="history-table__header-selector">
              <Checkbox v-model="selectAll" />
            </th>
            <th
              v-for="(col, index) in columnList"
              :key="col.id"
              :class="['history-table__column', { 'short-border': index !== 0 }]"
            >
              {{ col.name }}
            </th>
            <th
              v-if="hasScroll"
              class="history-table__scroll-spacer"
            />
          </tr>
        </thead>

        <tbody
          ref="body"
          :class="['history-table__body', { 'is-scrollable': hasScroll }]"
        >
          <tr
            v-for="entry in filteredEntries"
            :key="entry.id"
          >
            <td class="history-table__selector">
              <Checkbox v-model="entry.selected" />
            </td>
            <td>{{ entry.object }}</td>
            <td>{{ entry.action }}</td>
            <td>{{ entry.was }}</td>
            <td>{{ entry.became }}</td>
            <td>{{ entry.user }}</td>
            <td>{{ entry.time }}</td>
          </tr>
          <tr v-if="!filteredEntries.length">
            <td
              :colspan="columnList.length + 1"
              class="history-table__no-data"
            >
              {{ t("project_history_modal.not_found") }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped lang="scss">

.project-history {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.history-table {
  width: 100%;
  border: 1px solid #EEF1FF;
  border-radius: 8px;
  overflow: hidden;

  &__scroll-spacer {
    width: 10px;
    padding: 0;
    border: none;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }

  &__header th {
    padding: 12px 16px;
    background: #F4FAFF;
    font-weight: 500;
    color: #000000D9;
    position: relative;

    &.short-border::before {
      content: '';
      position: absolute;
      left: 0;
      top: 25%;
      height: 50%;
      width: 1px;
      background: #DEDEDE;
    }
  }

  &__body {
    display: block;
    max-height: 320px;
    overflow-y: auto;
    padding-right: 0;
    margin-right: 0;

    &::-webkit-scrollbar {
      width: 12px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background-color: #1A64D433;
      border-radius: 6px;
    }
  }

  .history-table__body.is-scrollable {
    padding-right: 10px;
    margin-right: 10px;
  }

  tr {
    display: table;
    width: 100%;
    table-layout: fixed;
  }

  td {
    padding: 12px 16px;
    vertical-align: middle;
    color: #898989;
    border-bottom: 1px solid #EEF1FF;

    &:last-child {
      border-right: 1px solid #EEF1FF;
    }
  }

  &__header-selector,
  &__selector {
    width: 18px;
    padding: 12px 16px;
  }

  &__no-data {
    text-align: center;
    padding: 20px 0;
    color: #999;
  }
}
</style>
